import { useState } from 'react'
import PropTypes from 'prop-types'
import { Tabs } from '@artprov/design-system'
import { TABS_LABELS } from '../../../common/constants/labels'
import { EVOLUCION_DENUNCIAS_CHART, GRAVEDAD_CHART, TIPO_ACCIDENTE_CHART } from '../../../common/constants/charts'
import TablasInforme from './tablasInforme/Index.jsx'
import { GraficosTab } from './GraficosTab.jsx'
import { DonutChart } from '../../../common/components/charts/DonutChart'
import { ColumnChart } from '../../../common/components/charts/ColumnChart'

const PDF_DONUT_CHART_HEIGHT = 456
const PDF_DONUT_CHART_OUTER_RADIUS = 137.5
const PDF_DONUT_CHART_HINT_OFFSET_Y = 40

/**
 * Tabs para mostrar los diferentes tipos de visualización del informe
 * @param {Array} data - Datos del informe
 */
const InformeTabs = ({
  topDenunciantes,
  topDenunciantesLoading,
  topJudiciales,
  topJudicialesLoading,
  topEvolucionSiniestros,
  topEvolucionSiniestrosLoading,
  topRankingSiniestros,
  topRankingSiniestrosLoading,
  indicadores,
  indicadoresLoading,
  graficosTipoAccidente,
  graficosTipoAccidenteLoading,
  graficosGravedad,
  graficosGravedadLoading,
  graficosDenuncias,
  graficosDenunciasLoading,
  graficosDenunciasShouldRender,
}) => {
  const TABS = [
    {
      id: 'graficos',
      text: TABS_LABELS.CHARTS,
      icon: '',
    },
    {
      id: 'tabla',
      text: TABS_LABELS.TABLE,
      icon: '',
    },
  ]

  const [selectedTab, setSelectedTab] = useState(TABS[0])

  return (
    <div className="w-full mt-6">
      {/* Contenedores ocultos fuera de pantalla — siempre montados para captura PDF */}
      <div aria-hidden="true" style={{ position: 'fixed', left: '-9999px', top: '-9999px', width: '560px' }}>
        <div data-chart-id="tipo-accidente">
          <DonutChart
            data={graficosTipoAccidente.data}
            title={TIPO_ACCIDENTE_CHART.title}
            leftHint={TIPO_ACCIDENTE_CHART.leftHint}
            rightHint={TIPO_ACCIDENTE_CHART.rightHint}
            ariaLabel={TIPO_ACCIDENTE_CHART.ariaLabel}
            showBorder={false}
            width="560px"
            minWidth={560}
            height={PDF_DONUT_CHART_HEIGHT}
            outerRadius={PDF_DONUT_CHART_OUTER_RADIUS}
            hintOffsetY={PDF_DONUT_CHART_HINT_OFFSET_Y}
          />
        </div>
        <div data-chart-id="tipo-gravedad">
          <DonutChart
            data={graficosGravedad.data}
            title={GRAVEDAD_CHART.title}
            leftHint={GRAVEDAD_CHART.leftHint}
            rightHint={GRAVEDAD_CHART.rightHint}
            ariaLabel={GRAVEDAD_CHART.ariaLabel}
            showBorder={false}
            width="560px"
            minWidth={560}
            height={PDF_DONUT_CHART_HEIGHT}
            outerRadius={PDF_DONUT_CHART_OUTER_RADIUS}
            hintOffsetY={PDF_DONUT_CHART_HINT_OFFSET_Y}
          />
        </div>
        {graficosDenunciasShouldRender && (
          <div data-chart-id="evolucion-denuncias">
            <ColumnChart
              categories={graficosDenuncias.categories}
              series={graficosDenuncias.series}
              title={EVOLUCION_DENUNCIAS_CHART.title}
              ariaLabel={EVOLUCION_DENUNCIAS_CHART.ariaLabel}
              width="560px"
              height={430}
            />
          </div>
        )}
      </div>

      <div className="border-b border-primary-10 my-2">
        <Tabs
          items={TABS}
          selected={selectedTab}
          setSelected={setSelectedTab}
          className="w-full"
          classNameTabButton="flex-1"
          underline={false}
        />
      </div>

      <div className="mt-4">
        {selectedTab.id === 'graficos' && (
          <GraficosTab
            topRankingSiniestros={topRankingSiniestros}
            topRankingSiniestrosLoading={topRankingSiniestrosLoading}
            indicadores={indicadores}
            indicadoresLoading={indicadoresLoading}
            graficosTipoAccidente={graficosTipoAccidente}
            graficosTipoAccidenteLoading={graficosTipoAccidenteLoading}
            graficosGravedad={graficosGravedad}
            graficosGravedadLoading={graficosGravedadLoading}
            graficosDenuncias={graficosDenuncias}
            graficosDenunciasLoading={graficosDenunciasLoading}
            graficosDenunciasShouldRender={graficosDenunciasShouldRender}
          />
        )}
        {selectedTab.id === 'tabla' && (
          <TablasInforme
            topDenunciantes={topDenunciantes}
            topDenunciantesLoading={topDenunciantesLoading}
            topJudiciales={topJudiciales}
            topJudicialesLoading={topJudicialesLoading}
            topEvolucionSiniestros={topEvolucionSiniestros}
            topEvolucionSiniestrosLoading={topEvolucionSiniestrosLoading}
          />
        )}
      </div>
    </div>
  )
}

InformeTabs.propTypes = {
  data: PropTypes.shape({
    fechaDesde: PropTypes.string,
    fechaHasta: PropTypes.string,
  }).isRequired,
  topDenunciantes: PropTypes.array.isRequired,
  topDenunciantesLoading: PropTypes.bool.isRequired,
  topJudiciales: PropTypes.array.isRequired,
  topJudicialesLoading: PropTypes.bool.isRequired,
  topEvolucionSiniestros: PropTypes.array.isRequired,
  topEvolucionSiniestrosLoading: PropTypes.bool.isRequired,
  topRankingSiniestros: PropTypes.array.isRequired,
  topRankingSiniestrosLoading: PropTypes.bool,
  indicadores: PropTypes.object.isRequired,
  indicadoresLoading: PropTypes.bool,
  graficosTipoAccidente: PropTypes.shape({
    total: PropTypes.number,
    data: PropTypes.array.isRequired,
  }).isRequired,
  graficosTipoAccidenteLoading: PropTypes.bool,
  graficosGravedad: PropTypes.shape({
    total: PropTypes.number,
    data: PropTypes.array.isRequired,
  }).isRequired,
  graficosGravedadLoading: PropTypes.bool,
  graficosDenuncias: PropTypes.shape({
    categories: PropTypes.array.isRequired,
    series: PropTypes.array.isRequired,
  }).isRequired,
  graficosDenunciasLoading: PropTypes.bool,
  graficosDenunciasShouldRender: PropTypes.bool,
}

export default InformeTabs
